import { h, JSX } from 'preact';
import { sendAnalyticsTrack } from '../../../../../common/js/TrackAnalyticsHelper';
import VastIcon from '../../vast-icon';
import {
  CloseItem,
  CrossCloseFullscreenControls,
  CtaCloseItem,
  CtaExpandItem,
  CtaFullscreenControls,
  DefaultFullscreenControls,
  ExpandItem,
  InformationItem,
  SoundItem
} from './sub-components';
import { progressBarStyle } from '../common/shared-style';
import { hexToRGBA } from '../../../../../common/js/colors';
import { isMobileWeb, isWeb } from '../../../../../common/js/environment-utils';
import { RootStore } from '../../../../../common/js/stores/root.store';
import { FullScreenControlsStrategy } from '../../../../../common/js/stores/controls/full-screen-controls-strategy';
import { VastIconType } from '../../../lib/vast/vast-icon';
import ProgressBar from '../../../../../common/components/progress-bar';
import OptOutIcon from '../../../../../common/components/opt-out/icon';

interface CtaConfig {
  useCta: boolean;
  reverseTopBar: boolean;
  backgroundColor: string;
  labelColor: string;
}

interface FullScreenProps {
  elapsedTime: number;
  remainingTime: number;
  useCrossCloseButton: boolean;
  onClose: (e: MouseEvent) => void;
  onOptout: () => void;
  onToggleExpand: (isExpanded: boolean) => void;
  onToggleMute: (e: Event) => void;
  videoDuration: number;
  isInterstitialAdUnit: boolean;
  isVideoMuted: boolean;
  showClose?: boolean;
  showRecommendation?: boolean;
  showCloseDelay: boolean;
  closingScheduledIn?: number;
  canInstallIcon?: boolean;
  closeButtonSize?: number;
  vastIcon?: VastIconType;
  onClickVastIcon?: (url: string) => void;
  cta: CtaConfig;
  onCtaClick: (e: MouseEvent) => void;
  showCloseDisabled: boolean;
  isFadeout: boolean;
  clickCloseTime?: number;
  showEndScreenClose: boolean;
  skOverlayIsOpened: boolean;
}

export default function FullScreen({
  elapsedTime,
  remainingTime,
  useCrossCloseButton,
  onClose,
  onOptout,
  onToggleExpand,
  onToggleMute,
  videoDuration,
  isInterstitialAdUnit,
  isVideoMuted,
  showClose = false,
  showRecommendation = true,
  showCloseDelay,
  closingScheduledIn,
  canInstallIcon = true,
  closeButtonSize = 100,
  vastIcon,
  onClickVastIcon,
  cta,
  onCtaClick,
  showCloseDisabled,
  isFadeout,
  clickCloseTime,
  showEndScreenClose,
  skOverlayIsOpened
}: FullScreenProps): JSX.Element {
  const controlsStore: FullScreenControlsStrategy = RootStore.getInstance()
    .controlsStore as FullScreenControlsStrategy;
  const videoStore = RootStore.getInstance().videoStore;
  const configStore = RootStore.getInstance().configStore;

  const { controls } = configStore.config;
  const isExpanded = videoStore.isVideoExpanded.value;
  const isDesktop = isWeb() && !isMobileWeb();
  const layerPadding = isDesktop && isExpanded ? 4 : 16;
  const iconSize = 18;
  const iconPadding = 9;
  const skOverlayPadding = 100;

  const styles = {
    container: {
      height: '100%',
      width: '100%'
    },
    topControls: {
      position: 'absolute' as const,
      top: layerPadding,
      left: layerPadding,
      right: layerPadding,
      alignItems: 'flex-start' as const
    },
    bottomControls: {
      position: 'absolute' as const,
      bottom: layerPadding + (skOverlayIsOpened ? skOverlayPadding : 0),
      left: layerPadding,
      right: layerPadding,
      alignItems: 'flex-end' as const
    }
  };

  const controlStyle = {
    backgroundColor: controls?.backgroundColor || '',
    backgroundOpacity: controls?.backgroundOpacity
      ? controls.backgroundOpacity / 100
      : 1,
    labelColor: controls?.labelColor || '',
    labelOpacity: controls?.labelOpacity ? controls.labelOpacity / 100 : 1
  };

  const canShowInformationLabel =
    !videoStore.isVideoCompleted.value &&
    Boolean(showRecommendation || canInstallIcon || showCloseDelay);

  const informationItem = (
    <InformationItem
      isInterstitialAdUnit={isInterstitialAdUnit}
      controlStyle={controlStyle}
      remainingTime={remainingTime}
      showRecommendation={showRecommendation}
      canInstallIcon={canInstallIcon}
      useCrossCloseButton={useCrossCloseButton}
    />
  );

  const closeItem = (
    <CloseItem
      closeButtonSize={closeButtonSize}
      onClose={onClose}
      closingScheduledIn={closingScheduledIn}
      showCloseDelay={showCloseDelay}
      controlStyle={controlStyle}
      useCrossCloseButton={useCrossCloseButton}
      elapsedTime={elapsedTime}
      clickCloseTime={clickCloseTime}
    />
  );

  const expandItem = (
    <ExpandItem
      onToggleExpand={onToggleExpand}
      controlStyle={controlStyle}
      iconSize={iconSize}
      iconPadding={iconPadding}
    />
  );

  const ctaCloseItem = (
    <CtaCloseItem
      closeButtonSize={closeButtonSize}
      onClose={onClose}
      closingScheduledIn={closingScheduledIn}
      showCloseDelay={showCloseDelay}
      controlStyle={controlStyle}
      remainingTime={remainingTime}
      showRecommendation={showRecommendation}
      canInstallIcon={canInstallIcon}
      isReversed={cta.reverseTopBar}
      // TODO refactoring this to use controls store
      showClose={showClose || showEndScreenClose}
      showCloseDisabled={showCloseDisabled && !showClose && !showEndScreenClose}
      elapsedTime={elapsedTime}
      clickCloseTime={clickCloseTime}
    />
  );

  const ctaExpandItem = (
    <CtaExpandItem
      onToggleExpand={onToggleExpand}
      controlStyle={controlStyle}
      remainingTime={remainingTime}
      showRecommendation={showRecommendation}
      canInstallIcon={canInstallIcon}
      isReversed={isDesktop && !cta.useCta ? false : cta.reverseTopBar}
    />
  );

  const soundItem = (
    <SoundItem
      onToggleMute={onToggleMute}
      controlStyle={controlStyle}
      iconSize={iconSize}
      isVideoMuted={isVideoMuted}
      isInterstitialAdUnit={isInterstitialAdUnit}
      iconPadding={iconPadding}
    />
  );

  const progressBarItem = (
    <ProgressBar
      id="progress"
      adjustedFormulae={true}
      elapsedTime={elapsedTime}
      duration={videoDuration}
      style={progressBarStyle()}
      onClick={(e: MouseEvent) => {
        sendAnalyticsTrack(e);
      }}
    />
  );

  const optOutItem = (
    <OptOutIcon
      iconSize={iconSize}
      onClick={onOptout}
      style={{
        padding: 9,
        background: hexToRGBA(
          controlStyle.backgroundColor,
          controlStyle.backgroundOpacity
        ) as string,
        color: hexToRGBA(
          controlStyle.labelColor,
          controlStyle.labelOpacity
        ) as string
      }}
      isInterstitialAdUnit={isInterstitialAdUnit}
    />
  );

  const vastIconItem = vastIcon ? (
    <VastIcon onClick={onClickVastIcon} icon={vastIcon} />
  ) : null;

  let selectedFullscreenControl;
  if (cta.useCta) {
    selectedFullscreenControl = (
      <CtaFullscreenControls
        cta={cta}
        ctaCloseItem={ctaCloseItem}
        ctaExpandItem={ctaExpandItem}
        customStyles={styles}
        isFadeout={isFadeout}
        isInterstitialAdUnit={isInterstitialAdUnit}
        onCtaClick={onCtaClick}
        optOutItem={optOutItem}
        soundItem={soundItem}
      />
    );
  } else if (useCrossCloseButton) {
    selectedFullscreenControl = (
      <CrossCloseFullscreenControls
        ctaCloseItem={ctaCloseItem}
        ctaExpandItem={ctaExpandItem}
        customStyles={styles}
        isInterstitialAdUnit={isInterstitialAdUnit}
        optOutItem={optOutItem}
        soundItem={soundItem}
      />
    );
  } else {
    selectedFullscreenControl = (
      <DefaultFullscreenControls
        closeItem={closeItem}
        customStyles={styles}
        expandItem={isDesktop ? ctaExpandItem : expandItem}
        informationItem={isDesktop ? null : informationItem}
        optOutItem={optOutItem}
        showClose={showClose || showEndScreenClose}
        showExpand={controlsStore.canExpand()}
        showInformation={canShowInformationLabel}
        soundItem={soundItem}
      />
    );
  }

  return (
    <div>
      {controlsStore.shouldDisplayVastIcon() ? vastIconItem : ''}
      {selectedFullscreenControl}
      {controlsStore.shouldDisplayProgressBar() ? progressBarItem : ''}
    </div>
  );
}
